/**
 * Query Gateway Modal
 * ====================
 * Queries the UniFi gateway to discover all managed devices,
 * shows import status, and allows importing new devices.
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  X,
  Loader2,
  Network,
  Wifi,
  Shield,
  Router,
  Monitor,
  Camera,
  Bell,
  Volume2,
  BatteryCharging,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Check,
} from 'lucide-react'
import Button from '@/components/Button'
import { assetApi } from '@/services/api'
import type { UniFiDeviceSummary, QueryGatewayResponse } from '@/types'

interface QueryGatewayModalProps {
  isOpen: boolean
  onClose: () => void
}

const DEVICE_TYPE_ICONS: Record<string, React.ReactNode> = {
  switch: <Network size={16} className="text-blue-500" />,
  access_point: <Wifi size={16} className="text-green-500" />,
  firewall: <Shield size={16} className="text-purple-500" />,
  router: <Router size={16} className="text-orange-500" />,
  ups: <BatteryCharging size={16} className="text-yellow-500" />,
  camera: <Camera size={16} className="text-red-500" />,
  doorbell: <Bell size={16} className="text-red-400" />,
  chime: <Volume2 size={16} className="text-pink-500" />,
  other: <Monitor size={16} className="text-gray-500" />,
}

const DEVICE_TYPE_LABELS: Record<string, string> = {
  switch: 'Switch',
  access_point: 'AP',
  firewall: 'Gateway',
  router: 'Router',
  ups: 'UPS',
  camera: 'Camera',
  doorbell: 'Doorbell',
  chime: 'Chime',
  other: 'Other',
}

export default function QueryGatewayModal({ isOpen, onClose }: QueryGatewayModalProps) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<QueryGatewayResponse | null>(null)
  const [showImported, setShowImported] = useState(false)

  useEffect(() => {
    if (isOpen) {
      queryGateway()
    } else {
      setResult(null)
      setError(null)
      setShowImported(false)
    }
  }, [isOpen])

  const queryGateway = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await assetApi.queryUniFiGateway()
      if (response.data) {
        setResult(response.data)
      } else {
        setError('No data returned from gateway')
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to query gateway'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleImport = (device: UniFiDeviceSummary) => {
    // Navigate to asset form with prefill data
    navigate('/assets/new', {
      state: {
        prefill: device.auto_fill,
        source: 'unifi',
        returnPath: '/assets/unifi',
      },
    })
    onClose()
  }

  if (!isOpen) return null

  const newDevices = result?.devices.filter(d => !d.already_imported) ?? []
  const importedDevices = result?.devices.filter(d => d.already_imported) ?? []

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Network className="text-blue-500" size={20} />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Query UniFi Gateway
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Loader2 size={32} className="animate-spin text-blue-500" />
              <p className="text-gray-500 dark:text-gray-400">Querying UniFi gateway...</p>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <AlertCircle size={32} className="text-red-500" />
              <p className="text-red-600 dark:text-red-400">{error}</p>
              <Button variant="secondary" onClick={queryGateway}>
                Retry
              </Button>
            </div>
          )}

          {result && !loading && (
            <>
              {/* Summary */}
              <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Found <strong>{result.total_count}</strong> devices:
                  {result.new_count > 0 && (
                    <span className="text-blue-600 dark:text-blue-400">
                      {' '}{result.new_count} new
                    </span>
                  )}
                  {result.new_count > 0 && result.imported_count > 0 && ', '}
                  {result.imported_count > 0 && (
                    <span className="text-green-600 dark:text-green-400">
                      {result.imported_count} already imported
                    </span>
                  )}
                </p>
              </div>

              {/* New devices */}
              {newDevices.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    New Devices
                  </h3>
                  <div className="border dark:border-gray-700 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Device</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">IP</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Firmware</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {newDevices.map((device) => (
                          <tr key={device.mac} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                {DEVICE_TYPE_ICONS[device.device_type] || DEVICE_TYPE_ICONS.other}
                                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                  {DEVICE_TYPE_LABELS[device.device_type] || 'Other'}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {device.name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {device.model}
                              </p>
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-sm font-mono text-gray-700 dark:text-gray-300">
                                {device.ip}
                              </p>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                  {device.firmware}
                                </span>
                                {device.upgrade_available ? (
                                  <span className="w-2 h-2 bg-red-500 rounded-full" title="Update available" />
                                ) : (
                                  <span className="w-2 h-2 bg-green-500 rounded-full" title="Up to date" />
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Button
                                size="sm"
                                onClick={() => handleImport(device)}
                                icon={<ArrowRight size={14} />}
                              >
                                Import
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Already imported devices (collapsible) */}
              {importedDevices.length > 0 && (
                <div>
                  <button
                    onClick={() => setShowImported(!showImported)}
                    className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 mb-2"
                  >
                    {showImported ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    Already Imported ({importedDevices.length})
                  </button>
                  {showImported && (
                    <div className="border dark:border-gray-700 rounded-lg overflow-hidden opacity-75">
                      <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Device</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">IP</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Firmware</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {importedDevices.map((device) => (
                            <tr key={device.mac}>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  {DEVICE_TYPE_ICONS[device.device_type] || DEVICE_TYPE_ICONS.other}
                                  <span className="text-xs font-medium text-gray-500 dark:text-gray-500">
                                    {DEVICE_TYPE_LABELS[device.device_type] || 'Other'}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  {device.name}
                                </p>
                                <p className="text-xs text-gray-400 dark:text-gray-500">
                                  {device.model}
                                </p>
                              </td>
                              <td className="px-4 py-3">
                                <p className="text-sm font-mono text-gray-500 dark:text-gray-500">
                                  {device.ip}
                                </p>
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-sm text-gray-500 dark:text-gray-500">
                                  {device.firmware}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                                  <Check size={12} />
                                  Imported
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* No devices found */}
              {result.total_count === 0 && (
                <div className="text-center py-8">
                  <Network size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">
                    No UniFi devices found on the gateway
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t dark:border-gray-700 flex justify-end gap-2">
          {!loading && result && (
            <Button variant="secondary" onClick={queryGateway}>
              Refresh
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            {result ? 'Done' : 'Close'}
          </Button>
        </div>
      </div>
    </div>
  )
}
